import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { getMetricsForService, getMetricById } from "@/data/projectMetrics";
import { getCaseStudyBySlug } from "@/data/caseStudies";

/**
 * ProofStrip — contextual, evidence-first proof for Phase 3.1 service pages.
 *
 * Sources metrics from projectMetrics.js. Renders ONLY:
 *   - metrics where `approvedForPublicDisplay === true`, and
 *   - metrics whose `relevantFor[]` includes `serviceSlug`, and
 *   - (if supplied) metrics whose id is in `metricIds`.
 *
 * Outcomes and capabilities are rendered in visually distinct groups so
 * a capability (24/7 WhatsApp) is never presented as a performance improvement
 * (30 min → 10 sec).
 *
 * If no approved+relevant metric exists, renders NOTHING. Never fabricates.
 *
 * Props:
 *   serviceSlug   Phase 3.1 service slug (required)
 *   metricIds     optional explicit whitelist to keep each service page's
 *                 proof narrow (avoids mechanical copy across services)
 *   caseStudySlug slug of the underlying case study to link to (optional —
 *                 if omitted, uses the project attached to the first metric)
 *   kicker/title  overridable copy
 */
export default function ProofStrip({
  serviceSlug,
  metricIds = null,
  caseStudySlug: overrideCaseStudySlug = null,
  kicker = "Real systems, measurable impact",
  title = "The proof this actually works.",
}) {
  let relevant = getMetricsForService(serviceSlug);
  if (metricIds && metricIds.length > 0) {
    const allowed = new Set(metricIds);
    relevant = metricIds
      .map((id) => getMetricById(id))
      .filter((m) => m && m.approvedForPublicDisplay === true && allowed.has(m.id));
  }
  if (relevant.length === 0) return null;

  const outcomes = relevant.filter((m) => m.kind === "outcome" || !m.kind);
  const capabilities = relevant.filter((m) => m.kind === "capability");

  const projectSlug = overrideCaseStudySlug || relevant[0]?.project;
  const cs = projectSlug ? getCaseStudyBySlug(projectSlug) : null;
  const caseStudyLive = cs && cs.status === "published";

  return (
    <section
      className="py-14 md:py-20 bg-muted/30 border-y border-border/60"
      data-testid={`proof-strip-${serviceSlug}`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">{kicker}</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.1]">{title}</h2>
            {caseStudyLive && (
              <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                Every number below is drawn from a real CloudLab implementation of{" "}
                <span className="text-foreground font-medium">{cs.projectName}</span>. Numbers are contextual to that project —
                not generic guarantees.
              </p>
            )}
          </div>
          {caseStudyLive && (
            <Link
              to={`/case-studies/${cs.slug}`}
              data-testid={`proof-strip-${serviceSlug}-case-link`}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-primary/30 bg-primary/[0.06] hover:bg-primary/10 text-sm text-primary transition-colors"
            >
              See the {cs.projectName} case study
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>

        {outcomes.length > 0 && (
          <div className="mt-8">
            <ProofGroupLabel>Operational outcomes</ProofGroupLabel>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid={`proof-strip-${serviceSlug}-outcomes`}>
              {outcomes.map((m, i) => <ProofCard key={m.id} m={m} i={i} />)}
            </div>
          </div>
        )}

        {capabilities.length > 0 && (
          <div className="mt-10">
            <ProofGroupLabel>Product capabilities</ProofGroupLabel>
            <div
              className="mt-3 flex flex-wrap gap-3"
              data-testid={`proof-strip-${serviceSlug}-capabilities`}
            >
              {capabilities.map((m) => (
                <div
                  key={m.id}
                  className="inline-flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3"
                  data-testid={`metric-${m.id}`}
                >
                  <span className="font-heading font-bold text-lg text-foreground leading-none">{m.value}</span>
                  <span className="text-sm text-muted-foreground leading-tight">{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ProofGroupLabel({ children }) {
  return (
    <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground/80">{children}</div>
  );
}

function ProofCard({ m, i }) {
  const isBeforeAfter = m.before && m.after;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
      className="rounded-2xl border border-border bg-background p-5 md:p-6"
      data-testid={`metric-${m.id}`}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">{m.label}</div>
      {isBeforeAfter ? (
        <div className="mt-3 flex items-center gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">Before</div>
            <div className="font-heading font-bold text-base md:text-lg text-foreground/70 truncate">{m.before}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-primary/80">After</div>
            <div className="font-heading font-black text-xl md:text-2xl text-primary truncate">{m.after}</div>
          </div>
        </div>
      ) : (
        <div className="mt-2 font-heading font-black text-2xl md:text-3xl text-primary leading-none">
          {m.value}
        </div>
      )}
      {m.qualifier && (
        <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] text-foreground/55">{m.qualifier}</div>
      )}
    </motion.div>
  );
}
